import { supabase } from './supabase.js';

/**
 * Service for fetching real student marks data from Supabase
 */
export class SupabaseDataService {
  
  /**
   * Fetch all available classes from the database
   */
  static async getAvailableClasses() {
    try {
      const { data, error } = await supabase
        .from('saas_students_marks')
        .select('class_id')
        .order('class_id');
      
      if (error) throw error;
      
      // Extract unique class IDs and format them for display
      const uniqueClasses = [...new Set(data.map(item => item.class_id))];
      return uniqueClasses.map(classId => {
        // Extract readable class name from ID (e.g., "MC01X68-J1A" -> "J1A")
        const parts = classId.split('-');
        return parts.length > 1 ? parts[1] : classId;
      }).sort();
    } catch (error) {
      console.error('Error fetching classes:', error);
      return ['1A', '1B', '1C', '1D', '2A', '2B', '2C', '2D', '3A', '3B', '3C', '3D']; // Fallback
    }
  }

  /**
   * Fetch all available subjects from the database
   */
  static async getAvailableSubjects() {
    try {
      const { data, error } = await supabase
        .from('saas_students_marks')
        .select('subject_id')
        .order('subject_id');
      
      if (error) throw error;
      
      // Extract unique subjects and map to display names
      const uniqueSubjects = [...new Set(data.map(item => item.subject_id))];
      return uniqueSubjects.map(this.mapSubjectIdToName).sort();
    } catch (error) {
      console.error('Error fetching subjects:', error);
      return ['中文', '英文', '數學', '綜合科學', '歷史', '地理', '電腦', '視覺藝術', '音樂', '體育']; // Fallback
    }
  }

  /**
   * Fetch all available academic years from the database
   */
  static async getAvailableAcademicYears() {
    try {
      const { data, error } = await supabase
        .from('academic_years')
        .select('year_code')
        .order('year_code');
      
      if (error) throw error;
      
      const uniqueYears = data.map(item => item.year_code);
      return uniqueYears.sort();
    } catch (error) {
      console.error('Error fetching academic years:', error);
      return ['2020-2021', '2021-2022', '2022-2023', '2023-2024', '2024-2025', '2025-2026']; // Fallback with migrated years
    }
  }

  /**
   * Fetch student marks data based on filters
   */
  static async getStudentMarks(filters = {}) {
    try {
      let query = supabase.from('saas_students_marks').select('*');
      
      // Apply filters
      if (filters.classes && filters.classes.length > 0) {
        // Convert display names back to database IDs
        const classIds = filters.classes.map(className => {
          // Find matching class_id from database that ends with the display name
          return `%${className}`;
        });
        query = query.or(classIds.map(id => `class_id.like.${id}`).join(','));
      }
      
      if (filters.subjects && filters.subjects.length > 0) {
        const subjectIds = filters.subjects.map(this.mapSubjectNameToId);
        query = query.in('subject_id', subjectIds);
      }
      
      if (filters.academicYears && filters.academicYears.length > 0) {
        query = query.in('academic_year', filters.academicYears);
      }
      
      const { data, error } = await query.order('class_id').order('subject_id');
      
      if (error) throw error;
      
      return data || [];
    } catch (error) {
      console.error('Error fetching student marks:', error);
      return [];
    }
  }

  /**
   * Generate comparison data for box plots based on selected filters
   */
  static async generateComparisonData(filters, comparisonMode) {
    const rawData = await this.getStudentMarks(filters);
    
    if (!rawData || rawData.length === 0) {
      return [];
    }

    const groupedData = {};
    
    rawData.forEach(record => {
      let groupKey;
      
      switch (comparisonMode) {
        case 'class':
          // Extract display name from class_id
          const parts = record.class_id.split('-');
          groupKey = parts.length > 1 ? parts[1] : record.class_id;
          break;
        case 'subject':
          groupKey = this.mapSubjectIdToName(record.subject_id);
          break;
        case 'test':
          // We'll create separate entries for each test type
          ['UT1', 'UT2', 'E1', 'E2'].forEach(testType => {
            const testKey = testType;
            if (!groupedData[testKey]) {
              groupedData[testKey] = [];
            }
            
            let score;
            switch (testType) {
              case 'UT1':
                score = record.first_ut_score;
                break;
              case 'UT2':
                score = record.second_ut_score;
                break;
              case 'E1':
                score = record.first_exam_score;
                break;
              case 'E2':
                score = record.second_exam_score;
                break;
            }
            
            if (score !== null && score !== undefined) {
              groupedData[testKey].push(score);
            }
          });
          return Object.keys(groupedData).map(key => ({
            name: key,
            data: groupedData[key],
            ...this.calculateBoxPlotStats(groupedData[key])
          }));
        case 'year':
          groupKey = record.academic_year;
          break;
        default:
          groupKey = record.class_id;
      }
      
      if (comparisonMode !== 'test') {
        if (!groupedData[groupKey]) {
          groupedData[groupKey] = [];
        }
        
        // Use year_total as the primary comparison metric
        if (record.year_total !== null && record.year_total !== undefined) {
          groupedData[groupKey].push(parseFloat(record.year_total));
        }
      }
    });

    // Convert grouped data to comparison format
    return Object.keys(groupedData).map(key => ({
      name: key,
      data: groupedData[key],
      ...this.calculateBoxPlotStats(groupedData[key])
    }));
  }

  /**
   * Calculate box plot statistics for a dataset
   */
  static calculateBoxPlotStats(data) {
    if (!data || data.length === 0) {
      return {
        min: 0,
        q1: 0,
        median: 0,
        q3: 0,
        max: 0,
        mean: 0,
        iqr: 0,
        outliers: []
      };
    }

    const sorted = [...data].sort((a, b) => a - b);
    const n = sorted.length;
    
    const min = sorted[0];
    const max = sorted[n - 1];
    const mean = data.reduce((sum, val) => sum + val, 0) / n;
    
    // Calculate quartiles
    const q1Index = Math.floor(n * 0.25);
    const medianIndex = Math.floor(n * 0.5);
    const q3Index = Math.floor(n * 0.75);
    
    const q1 = sorted[q1Index];
    const median = n % 2 === 0 ? (sorted[medianIndex - 1] + sorted[medianIndex]) / 2 : sorted[medianIndex];
    const q3 = sorted[q3Index];
    const iqr = q3 - q1;
    
    // Calculate outliers
    const lowerBound = q1 - 1.5 * iqr;
    const upperBound = q3 + 1.5 * iqr;
    const outliers = data.filter(val => val < lowerBound || val > upperBound);
    
    return {
      min: Math.round(min * 100) / 100,
      q1: Math.round(q1 * 100) / 100,
      median: Math.round(median * 100) / 100,
      q3: Math.round(q3 * 100) / 100,
      max: Math.round(max * 100) / 100,
      mean: Math.round(mean * 100) / 100,
      iqr: Math.round(iqr * 100) / 100,
      outliers: outliers.map(val => Math.round(val * 100) / 100)
    };
  }

  /**
   * Map subject ID to display name
   */
  static mapSubjectIdToName(subjectId) {
    const subjectMap = {
      'chinese': '中文',
      'english': '英文',
      'mathematics': '數學',
      'liberalStudies': '通識教育',
      'physics': '物理',
      'chemistry': '化學',
      'biology': '生物',
      'history': '歷史',
      'chinesehistory': '中國歷史',
      'geography': '地理',
      'economics': '經濟',
      'bafs': '企業、會計與財務概論',
      'ict': '資訊及通訊科技',
      'computerStudies': '電腦',
      'visualArts': '視覺藝術',
      'music': '音樂',
      'pe': '體育',
      'hkdsepe': '體育(文憑試)',
      'religiousStudies': '宗教研究',
      'socialStudies': '社會研究',
      'science': '綜合科學',
      'management': '管理'
    };
    return subjectMap[subjectId] || subjectId;
  }

  /**
   * Map subject display name to ID
   */
  static mapSubjectNameToId(subjectName) {
    const reverseMap = {
      '中文': 'chinese',
      '英文': 'english',
      '數學': 'mathematics',
      '通識教育': 'liberalStudies',
      '物理': 'physics',
      '化學': 'chemistry',
      '生物': 'biology',
      '歷史': 'history',
      '中國歷史': 'chinesehistory',
      '地理': 'geography',
      '經濟': 'economics',
      '企業、會計與財務概論': 'bafs',
      '資訊及通訊科技': 'ict',
      '電腦': 'computerStudies',
      '視覺藝術': 'visualArts',
      '音樂': 'music',
      '體育': 'pe',
      '體育(文憑試)': 'hkdsepe',
      '宗教研究': 'religiousStudies',
      '社會研究': 'socialStudies',
      '綜合科學': 'science',
      '管理': 'management'
    };
    return reverseMap[subjectName] || subjectName;
  }
}

export default SupabaseDataService;
